import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import CarCard from "../components/CarCard";
import CarsMap from "../components/CarsMap";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Search, SlidersHorizontal, Zap, Map as MapIcon, LayoutGrid } from "lucide-react";

const CATEGORIES = ["economy", "sedan", "suv", "luxury", "sports", "ev", "truck"];

export default function Browse() {
  const [params, setParams] = useSearchParams();
  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [city, setCity] = useState(params.get("city") || "");
  const [category, setCategory] = useState(params.get("category") || "");
  const [gigOnly, setGigOnly] = useState(params.get("gig_ready") === "true");
  const [view, setView] = useState(params.get("view") || "grid");

  const fetchCars = async () => {
    setLoading(true);
    const q = {};
    if (city) q.city = city;
    if (category) q.category = category;
    if (gigOnly) q.gig_ready = true;
    try {
      const { data } = await api.get("/cars", { params: q });
      setCars(data);
    } finally { setLoading(false); }
  };

  // city is applied explicitly via the search button, so it is intentionally excluded
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchCars(); }, [category, gigOnly]);

  const apply = (e) => {
    e?.preventDefault();
    const p = {};
    if (city) p.city = city;
    if (category) p.category = category;
    if (gigOnly) p.gig_ready = "true";
    if (view !== "grid") p.view = view;
    setParams(p);
    fetchCars();
  };

  return (
    <main className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-10" data-testid="browse-page">
      <div className="mb-8 flex items-end justify-between flex-wrap gap-4 rise">
        <div>
          <p className="font-mono text-xs tracking-widest uppercase text-cyan-400 mb-2">// Marketplace</p>
          <h1 className="font-display text-4xl md:text-5xl font-extrabold tracking-tight">Find your ride.</h1>
        </div>
        <div className="inline-flex rounded-full border border-slate-800 bg-slate-900/60 p-1" data-testid="view-switcher">
          <button onClick={() => setView("grid")} data-testid="view-grid"
            className={`px-3 py-1.5 rounded-full flex items-center gap-1.5 text-sm font-medium transition-all ${view === "grid" ? "bg-white text-slate-950" : "text-slate-300 hover:text-white"}`}>
            <LayoutGrid className="w-3.5 h-3.5" /> Grid
          </button>
          <button onClick={() => setView("map")} data-testid="view-map"
            className={`px-3 py-1.5 rounded-full flex items-center gap-1.5 text-sm font-medium transition-all ${view === "map" ? "bg-white text-slate-950" : "text-slate-300 hover:text-white"}`}>
            <MapIcon className="w-3.5 h-3.5" /> Map
          </button>
        </div>
      </div>

      <form onSubmit={apply} className="glass rounded-2xl p-4 sm:p-5 mb-8 flex flex-col lg:flex-row gap-3 rise rise-1" data-testid="browse-filters">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <Input value={city} onChange={(e) => setCity(e.target.value)} placeholder="Search by city…"
            data-testid="browse-city-input" className="h-12 pl-10 bg-slate-900 border-slate-800" />
        </div>
        <Button type="button" onClick={() => setGigOnly(!gigOnly)} data-testid="toggle-gig-only"
          variant={gigOnly ? "default" : "outline"}
          className={`h-12 ${gigOnly ? "bg-emerald-500 text-slate-950 hover:bg-emerald-400" : "bg-transparent border-slate-700 hover:bg-slate-800"}`}>
          <Zap className="w-4 h-4 mr-2" /> Gig-ready only
        </Button>
        <Button type="submit" className="h-12 bg-white text-slate-950 hover:bg-slate-100 font-semibold" data-testid="browse-search-btn">
          <SlidersHorizontal className="w-4 h-4 mr-2" /> Search
        </Button>
      </form>

      <div className="flex flex-wrap gap-2 mb-8" data-testid="category-chips">
        <button onClick={() => setCategory("")} data-testid="cat-all"
          className={`px-4 py-1.5 rounded-full text-xs font-mono uppercase tracking-widest border transition-all ${!category ? "bg-white text-slate-950 border-white" : "border-slate-700 text-slate-300 hover:border-slate-500"}`}>All</button>
        {CATEGORIES.map((c) => (
          <button key={c} onClick={() => setCategory(c)} data-testid={`cat-${c}`}
            className={`px-4 py-1.5 rounded-full text-xs font-mono uppercase tracking-widest border transition-all ${category === c ? "bg-white text-slate-950 border-white" : "border-slate-700 text-slate-300 hover:border-slate-500"}`}>{c}</button>
        ))}
      </div>

      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {[...Array(6)].map((_, i) => <div key={`skeleton-${i}`} className="aspect-[4/3] bg-slate-900 rounded-2xl animate-pulse" />)}
        </div>
      ) : cars.length === 0 ? (
        <div className="text-center py-24">
          <p className="text-slate-400 text-lg">No cars match these filters.</p>
          <Badge className="mt-4 bg-slate-800 text-slate-300">Try widening your search.</Badge>
        </div>
      ) : view === "map" ? (
        <CarsMap cars={cars} />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="car-grid">
          {cars.map((c, i) => <CarCard key={c.car_id} car={c} delay={i * 50} />)}
        </div>
      )}
    </main>
  );
}
